import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, FileCheck, Package, Ship, Globe, Warehouse, Radar, Handshake, Search } from "lucide-react";
import { KineticLine, Reveal, Overline } from "@/components/Kinetic";
import { IMAGES, SUPPLY_CHAIN, CAPABILITIES, TRADE_DOCUMENTS, INCOTERMS, PACKAGING, PACKAGING_FEATURES, INDUSTRIES } from "@/data/site";

const CAP_ICONS = [Globe, Ship, FileCheck, Search, Warehouse, Radar];

export default function ExportLogistics() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <main data-testid="export-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[70vh] items-end overflow-hidden" data-testid="export-hero">
        <motion.img
          src={IMAGES.containers}
          alt="Container port at sunset"
          style={{ y: imgY, scale: 1.1 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-forest/70" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-forest to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
          <Overline>Export & Logistics</Overline>
          <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-cream" data-testid="export-heading">
            <KineticLine delay={0.25} className="text-5xl md:text-6xl lg:text-7xl">
              Efficient. Reliable.
            </KineticLine>
            <KineticLine delay={0.4} className="text-5xl md:text-6xl lg:text-7xl">
              Global<span className="text-gold">.</span>
            </KineticLine>
          </h1>
          <Reveal delay={0.7} className="mt-6 max-w-2xl">
            <p className="text-base font-light leading-relaxed text-sage md:text-lg">
              Seamless movement of goods across borders through our trusted logistics network,
              compliance expertise and global partnerships.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 9-STEP SUPPLY CHAIN */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="export-supply-chain">
        <Reveal><Overline>Our End-to-End Supply Chain</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
            From our farms, through quality processing, to the world.
          </h2>
        </Reveal>
        <div className="mt-16 space-y-0 border-t border-forest-border">
          {SUPPLY_CHAIN.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.04}>
              <div className="group grid items-center gap-4 border-b border-forest-border py-8 transition-colors duration-500 hover:bg-forest-surface md:grid-cols-12 md:px-6" data-testid={`export-step-${s.step}`}>
                <span className="font-serif text-5xl font-light text-gold/30 transition-colors duration-500 group-hover:text-gold md:col-span-2 md:text-6xl">
                  {s.step}
                </span>
                <h3 className="font-serif text-2xl font-light text-cream md:col-span-4">{s.title}</h3>
                <p className="text-sm font-light leading-relaxed text-sage md:col-span-6">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="border-y border-forest-border bg-forest-surface py-28" data-testid="export-capabilities">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><Overline>Our Capabilities</Overline></Reveal>
          <div className="mt-12 grid gap-px border border-forest-border bg-forest-border sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => {
              const Icon = CAP_ICONS[i];
              return (
                <Reveal key={c.title} delay={i * 0.08} className="bg-forest-surface">
                  <div className="group h-full px-8 py-10 transition-colors duration-500 hover:bg-forest-light" data-testid={`capability-${i}`}>
                    <Icon className="h-7 w-7 text-gold" strokeWidth={1.25} />
                    <h3 className="mt-5 text-lg font-semibold tracking-wide text-cream">{c.title}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-sage">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DOCUMENTS + INCOTERMS */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="export-docs">
        <div className="grid gap-16 lg:grid-cols-2">
          <div>
            <Reveal><Overline>Trade Documentation</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-3xl font-light text-cream md:text-4xl">
                Transparent, compliant & efficient transactions
              </h2>
            </Reveal>
            <div className="mt-10 space-y-0 border-t border-forest-border">
              {TRADE_DOCUMENTS.map((d, i) => (
                <Reveal key={d} delay={i * 0.05}>
                  <div className="flex items-center gap-4 border-b border-forest-border py-4" data-testid={`doc-${i}`}>
                    <FileCheck className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                    <span className="text-sm font-light text-cream">{d}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <Reveal><Overline>Commercial Terms We Offer</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-3xl font-light text-cream md:text-4xl">
                Flexible Incoterms, tailored to your business
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {INCOTERMS.map((t, i) => (
                <Reveal key={t.code} delay={i * 0.06}>
                  <div className="group h-full border border-forest-border bg-forest-surface px-6 py-6 transition-colors duration-500 hover:border-gold/40" data-testid={`incoterm-${t.code.toLowerCase()}`}>
                    <p className="font-serif text-2xl font-light text-gold">{t.code}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.15em] text-cream/80">{t.name}</p>
                    <p className="mt-3 text-sm font-light leading-relaxed text-sage">{t.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGING */}
      <section className="border-y border-forest-border bg-forest-surface py-28" data-testid="export-packaging">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal><Overline>Our Packaging Solutions</Overline></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-serif text-3xl font-light leading-tight text-cream md:text-4xl">
                  Protect. Perform. Sustain. Present.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <ul className="mt-8 space-y-3">
                  {PACKAGING_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm font-light text-sage">
                      <Package className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
                      {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="grid gap-px border border-forest-border bg-forest-border sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3">
              {PACKAGING.map((p, i) => (
                <Reveal key={p.name} delay={i * 0.06} className="bg-forest">
                  <div className="h-full px-6 py-8 transition-colors duration-500 hover:bg-forest-light" data-testid={`packaging-${i}`}>
                    <Package className="h-6 w-6 text-gold/70" strokeWidth={1.25} />
                    <h3 className="mt-4 text-sm font-semibold tracking-wide text-cream">{p.name}</h3>
                    <p className="mt-1 text-xs text-gold/80">{p.spec}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="export-industries">
        <Reveal><Overline>Industries We Serve</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
            Agro-commodity solutions for every sector
          </h2>
        </Reveal>
        <div className="mt-14 flex flex-wrap gap-3">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind} delay={i * 0.04}>
              <span className="inline-flex items-center gap-2 border border-forest-border bg-forest-surface px-5 py-3 text-sm font-light text-cream transition-colors duration-300 hover:border-gold/50 hover:text-gold" data-testid={`industry-${i}`}>
                <Handshake className="h-3.5 w-3.5 text-gold/70" strokeWidth={1.5} />
                {ind}
              </span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-16">
          <Link
            to="/contact"
            data-testid="export-cta-button"
            className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream"
          >
            Discuss Your Requirements
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
